import {
  formatCurrency,
  formatTime,
  maskPhoneNumber,
  getPhoneNumberCountryCodes
} from '@smarttransit/common'
import {
  filterTripsPastPrepaidDestination,
  getBoardedPassengerProfileIds,
  getValidTicketForTrip,
  hasPrepaidTripPassedDestination,
  isPassengerPaid,
  onlyTripsAboardVehicle,
  resolveLowBalanceFlags,
  resolvePassengerPayStatusPresentation,
  resolvePassengerListSortTimestamp,
  sortPassengerListRows
} from '@smarttransit/common-client'

const THEME = {
  success: '#4CAF50',
  warning: '#FFC107',
  error: '#f65122'
}

function formatLivePassengerRow (passengerTrip, boardedPassenger) {
  const passengerProfile = passengerTrip.passengerProfile || {}
  const identifiers = Array.isArray(passengerProfile.passengerIdentifiers)
    ? passengerProfile.passengerIdentifiers
    : []
  const passengerIdentifier = identifiers[0] || {}
  const ticket = getValidTicketForTrip(passengerTrip)
  const phoneNumber = passengerIdentifier.phoneNumber || null
  const userPayStatus = isPassengerPaid(passengerTrip)
  const { balance, lowBalance, extremelyLowBalance } = resolveLowBalanceFlags(passengerTrip, { userPayStatus })
  const payStatusPresentation = resolvePassengerPayStatusPresentation({
    userPayStatus,
    extremelyLowBalance,
    lowBalance,
    colors: THEME
  })
  const sortTimestamp = resolvePassengerListSortTimestamp({ passengerTrip })

  let amount = passengerTrip.cost
  let prepaidGuest = false
  let expiresTimestamp = null
  let previousReconfirmCode = null

  if (ticket) {
    amount = ticket.amount * -1
    prepaidGuest = !!(ticket.logMetadata && ticket.logMetadata.prepaidGuest)
    previousReconfirmCode = ticket.logMetadata && ticket.logMetadata.reconfirmCode
      ? ticket.logMetadata.reconfirmCode
      : null

    const rawExpires = ticket.logMetadata && ticket.logMetadata.expires
    if (rawExpires && Date.now() > new Date(rawExpires).getTime()) {
      expiresTimestamp = rawExpires
    }
  }

  const reconfirmCode = boardedPassenger && boardedPassenger.reconfirmcode
    ? String(boardedPassenger.reconfirmcode)
    : null

  return {
    passengerProfileId: passengerTrip.passengerProfileId,
    passengerTripId: passengerTrip.id,
    time: formatTime(passengerTrip.dateUpdated || passengerTrip.dateCreated),
    dateUpdatedTimestamp: new Date(passengerTrip.dateUpdated || passengerTrip.dateCreated).getTime(),
    dateCreated: passengerTrip.datePrepaid || (ticket && ticket.dateCreated) || passengerTrip.dateCreated,
    sortTimestamp,
    balance,
    passengerTrip,
    phoneNumber,
    phoneNumberLabel: phoneNumber
      ? maskPhoneNumber(phoneNumber, getPhoneNumberCountryCodes())
      : (passengerTrip.passengerProfileId || 'Unknown'),
    amount,
    amountLabel: formatCurrency(amount),
    userPayStatus,
    lowBalance,
    extremelyLowBalance,
    passengerTripError: passengerTrip.metadata && passengerTrip.metadata.lastError
      ? passengerTrip.metadata.lastError
      : null,
    balanceLabel: formatCurrency(balance),
    ...payStatusPresentation,
    prepaid: Boolean(passengerTrip.datePrepaid),
    prepaidGuest,
    reconfirmCode: reconfirmCode || (previousReconfirmCode ? String(previousReconfirmCode) : null),
    expiresTimestamp,
    unresolvedPassengerScan: false
  }
}

/**
 * Build display rows for the admin live passengers dialog from Redis passenger hash + trip records.
 */
function buildLivePassengerDisplayRows ({
  passengerHash,
  passengerTrips,
  transportationProfileId,
  busStops,
  lastBusStopId
}) {
  const boardedPassengerProfileIds = getBoardedPassengerProfileIds(passengerHash)
  const boardedByProfileId = {}

  ;(Array.isArray(passengerHash) ? passengerHash : []).forEach((passenger) => {
    if (passenger && passenger.profileid) {
      boardedByProfileId[passenger.profileid] = passenger
    }
  })

  const tripsBeforeDestinationFilter = onlyTripsAboardVehicle(passengerTrips, {
    transportationProfileId,
    boardedPassengerProfileIds
  })
  const tripsAboard = filterTripsPastPrepaidDestination(tripsBeforeDestinationFilter, {
    busStops,
    lastBusStopId
  })
  const clearedPrepaidProfileIds = new Set(
    tripsBeforeDestinationFilter
      .filter((passengerTrip) => hasPrepaidTripPassedDestination(passengerTrip, { busStops, lastBusStopId }))
      .map((passengerTrip) => passengerTrip.passengerProfileId)
      .filter(Boolean)
  )

  const rows = tripsAboard.map((passengerTrip) => {
    return formatLivePassengerRow(passengerTrip, boardedByProfileId[passengerTrip.passengerProfileId])
  })

  const tripProfileIds = new Set(rows.map((o) => o.passengerProfileId).filter(Boolean))
  boardedPassengerProfileIds.forEach((profileId) => {
    if (tripProfileIds.has(profileId) || clearedPrepaidProfileIds.has(profileId)) {
      return
    }

    const boardedPassenger = boardedByProfileId[profileId] || {}
    const scanDateCreated = boardedPassenger.timestamp || new Date().toISOString()
    const timestamp = new Date(scanDateCreated).getTime()

    rows.push({
      passengerProfileId: profileId,
      passengerTripId: boardedPassenger.tripid || null,
      time: formatTime(scanDateCreated),
      dateUpdatedTimestamp: timestamp,
      dateCreated: scanDateCreated,
      sortTimestamp: timestamp,
      balance: null,
      phoneNumber: null,
      phoneNumberLabel: profileId,
      amount: null,
      amountLabel: '—',
      userPayStatus: false,
      lowBalance: false,
      extremelyLowBalance: false,
      passengerTripError: null,
      balanceLabel: '—',
      balanceLowThemeColor: THEME.warning,
      showPayStatusCheck: false,
      prepaid: false,
      prepaidGuest: false,
      reconfirmCode: boardedPassenger.reconfirmcode ? String(boardedPassenger.reconfirmcode) : null,
      expiresTimestamp: null,
      unresolvedPassengerScan: true
    })
  })

  return sortPassengerListRows(rows)
}

export {
  buildLivePassengerDisplayRows
}
